/**
 * useWorkspaceKeyboard — Keyboard shortcuts for the workspace
 *
 * Extracted from MainWorkspace to reduce component size.
 * Handles: Cmd+Z/Shift+Z (undo/redo), Cmd+E (view cycle),
 * Alt+Arrow (content history), Arrow keys (thread nav),
 * and Electron content-navigate IPC events.
 *
 * @module @humanizer/studio/hooks/useWorkspaceKeyboard
 */

import { useEffect } from 'react';
import type { ViewMode, WorkspaceMode } from '../components/workspace/workspace-types';

export interface UseWorkspaceKeyboardOptions {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  /** Current workspace mode for mode-aware Cmd+E cycling */
  workspaceMode: WorkspaceMode;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  /** Whether there is content to enter edit mode */
  hasContent: boolean;
  handleEnterEdit: () => void;
  /** Whether split content is available */
  hasSplitContent: boolean;
  /** Whether draft text is available */
  hasDraftText: boolean;
  /** Stack edit mode toggle */
  stackEditMode: boolean;
  setStackEditMode: (fn: (prev: boolean) => boolean) => void;
  /** Thread mode state */
  isThreadMode: boolean;
  goPrev: () => void;
  goNext: () => void;
  goFirst: () => void;
  goLast: () => void;
  /** Content history navigation */
  contentGoBack: () => void;
  contentGoForward: () => void;
}

export function useWorkspaceKeyboard({
  viewMode,
  setViewMode,
  workspaceMode,
  canUndo,
  canRedo,
  undo,
  redo,
  hasContent,
  handleEnterEdit,
  hasSplitContent,
  hasDraftText,
  stackEditMode,
  setStackEditMode,
  isThreadMode,
  goPrev,
  goNext,
  goFirst,
  goLast,
  contentGoBack,
  contentGoForward,
}: UseWorkspaceKeyboardOptions) {
  // Main keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          if (canRedo) redo();
        } else {
          e.preventDefault();
          if (canUndo) undo();
        }
      }
      // Cmd+E to cycle view modes within the active workspace mode
      // Archive: read -> edit -> split (if available) -> read
      // Project: stack -> book-plan -> outline -> draft (if available) -> stack
      // In stack mode: toggles card editing without leaving stack
      if ((e.metaKey || e.ctrlKey) && e.key === 'e') {
        e.preventDefault();
        if (workspaceMode === 'archive') {
          if (viewMode === 'read') {
            if (hasContent) handleEnterEdit();
          } else if (viewMode === 'edit') {
            if (hasSplitContent) {
              setViewMode('split');
            } else {
              setViewMode('read');
            }
          } else if (viewMode === 'split') {
            setViewMode('read');
          }
        } else {
          // Project mode cycling
          if (viewMode === 'stack') {
            if (stackEditMode) {
              setStackEditMode(prev => !prev);
            } else {
              setViewMode('book-plan');
            }
          } else if (viewMode === 'book-plan') {
            setViewMode('outline');
          } else if (viewMode === 'outline') {
            if (hasDraftText) {
              setViewMode('draft');
            } else {
              setViewMode('stack');
            }
          } else if (viewMode === 'draft') {
            setViewMode('stack');
          }
        }
      }
      // Alt+Arrow for content history navigation
      if (e.altKey && e.key === 'ArrowLeft') { e.preventDefault(); contentGoBack(); return; }
      if (e.altKey && e.key === 'ArrowRight') { e.preventDefault(); contentGoForward(); return; }

      // Arrow keys for thread navigation (only in read mode, not in text inputs)
      if (isThreadMode && viewMode === 'read') {
        const target = e.target as HTMLElement;
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
        if (!isInput) {
          if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
          if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
          if (e.key === 'Home') { e.preventDefault(); goFirst(); }
          if (e.key === 'End') { e.preventDefault(); goLast(); }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, undo, redo, viewMode, workspaceMode, hasContent, hasSplitContent, handleEnterEdit, hasDraftText, isThreadMode, goPrev, goNext, goFirst, goLast, contentGoBack, contentGoForward, stackEditMode, setStackEditMode, setViewMode]);

  // Electron: main process intercepts Alt+Arrow (before-input-event) to prevent
  // Chromium's built-in back/forward navigation, then forwards via IPC as a
  // 'content-navigate' DOM event. The keydown handler above is the web fallback.
  useEffect(() => {
    const handler = (e: Event) => {
      const direction = (e as CustomEvent).detail;
      if (direction === 'back') contentGoBack();
      else if (direction === 'forward') contentGoForward();
    };
    window.addEventListener('content-navigate', handler);
    return () => window.removeEventListener('content-navigate', handler);
  }, [contentGoBack, contentGoForward]);
}
